import { wrapCreateBrowserRouter } from '@sentry/react';

import React from 'react';
import { createBrowserRouter, Navigate, RouteObject, useLocation } from 'react-router-dom';

import { AppNameEnum, RoleEnum } from '_lib/api/apiTypes.shim';
import { CallPop } from '_lib/callPop/CallPop';
import { RouterProvider } from '_lib/components/RouterProvider/RouterProvider';
import { AccountListRoute, AccountDetailRoute } from '_lib/featureComponents/Account';
import { AccountUserDetailRoute } from '_lib/featureComponents/AccountUser/AccountUserDetail';
import { AccountUserListRoute } from '_lib/featureComponents/AccountUser/AccountUserList';
import { AiCallDetailRoute } from '_lib/featureComponents/AiCall/AiCallDetail';
import { AiCallListRoute } from '_lib/featureComponents/AiCall/AiCallList';
import { AppRoot } from '_lib/featureComponents/App/AppRoot';
import { AuthSuccessResult } from '_lib/featureComponents/auth/auth.types';
import { AuthResourceConfig } from '_lib/featureComponents/auth/authContext';
import { authRouteCheck } from '_lib/featureComponents/auth/authRouteChecker';
import {
  CallCenterAdmin,
  CallTakingEnabledForAccount,
  ManageAccountUsers,
  ManageCallCenterUsers,
  ManageCompanies,
  SystemUser,
  ViewAccounts,
  ViewAccountUsers,
  ViewCallCenterUsers
} from '_lib/featureComponents/auth/permissionHelpers';
import { CallCenterDetailRoute } from '_lib/featureComponents/CallCenter/CallCenterDetail';
import { CallCenterListRoute } from '_lib/featureComponents/CallCenter/CallCenterList';
import { CompanyDetailRoute } from '_lib/featureComponents/Company/CompanyDetailRoute';
import { CompanyListRoute } from '_lib/featureComponents/Company/CompanyList';
import { MyCallCenterRoute } from '_lib/featureComponents/MyCallCenter/MyCallCenter';
import { appRoutePaths, getFullAppRoutePath } from '_lib/featureComponents/Nav/nav.types';
import { PhoneCallDetailRoute } from '_lib/featureComponents/PhoneCall/PhoneCallDetail/PhoneCallDetail';
import { PhoneCallListRoute } from '_lib/featureComponents/PhoneCall/PhoneCallList';
import { ProfileRoute } from '_lib/featureComponents/Profile/Profile';
import { ForbiddenRoute } from '_lib/featureComponents/Routing/ForbiddenRoute';
import { RouteErrorBoundary, RouteErrorBoundaryProps } from '_lib/featureComponents/Routing/RouteErrorBoundary';
import { SystemAdministrationRoute } from '_lib/featureComponents/SystemAdministration/SystemAdministration';
import { UserDetailRoute } from '_lib/featureComponents/User/UserDetail';
import { UserListRoute } from '_lib/featureComponents/User/UserList';
import { AuthStorage } from '_lib/localStorage/authStorage';
import { authRoutes } from '_lib/routes/authRoutes';

import { reportException } from '_lib/utils/sentryService';

import { RouterRoot } from './RouterRoot';

const sentryCreateBrowserRouter = wrapCreateBrowserRouter(createBrowserRouter);

const onAuthSuccess = async (authCache: AuthResourceConfig, result: AuthSuccessResult) => {
  if (result.user.role === RoleEnum.SYSTEM && !AuthStorage.currentCallCenterId.get()) {
    result.navigate(getFullAppRoutePath(appRoutePaths.CallCenter));
  } else if (result.user.accountId) {
    try {
      const account = await authCache.accountCacheResource.get();
      const callTakingEnabled = CallTakingEnabledForAccount({ user: result.user, account: account || undefined });
      result.navigate(getFullAppRoutePath(callTakingEnabled ? appRoutePaths.CallLog : appRoutePaths.Profile));
    } catch (error) {
      reportException(error, { extra: { authUser: result.user } });
      result.navigate(getFullAppRoutePath(appRoutePaths.Profile));
    }
  } else {
    result.navigate(getFullAppRoutePath(appRoutePaths.CallLog));
  }
};

const DefaultRouteErrorElement = (props: RouteErrorBoundaryProps) => {
  const { navItems, ...rest } = props;
  return <RouteErrorBoundary navItems={navItems} {...rest} />;
};

const Redirects = (): React.ReactElement => {
  const location = useLocation();
  // TODO - we will need to redirect legacy links, IE:
  //      - /#/call-log/:phoneCallId (sendEscalationEmail)
  if (location && location.pathname.startsWith('/resetPassword/')) {
    return <Navigate to={location.pathname.replace('/resetPassword', 'auth/reset-password')} replace />;
  }
  return <Navigate to='auth/login' />;
};

// NOTE - `sentryCreateBrowserRouter` gives us a subset type of `RouteObject` for some reason, use
// the type from react-router-dom instead and pass it in. This should be fine, but test in case
// they did this for a GOOD reason - see https://github.com/getsentry/sentry-javascript/blob/master/packages/react/src/reactrouterv6.tsx#L337
const getRoutes: (authCache: AuthResourceConfig) => RouteObject[] = (authCache) => [
  {
    path: '/',
    element: <RouterRoot />,
    errorElement: <DefaultRouteErrorElement />,
    children: [
      ...authRoutes({
        appName: AppNameEnum.CALL_PILOT,
        onLogin: (result: AuthSuccessResult) => onAuthSuccess(authCache, result),
        onResetPassword: (result: AuthSuccessResult) => onAuthSuccess(authCache, result)
      }),
      {
        path: `/app/${appRoutePaths.CallPop}/:companyDNIS/:callerId`,
        element: <CallPop />,
        loader: async () => {
          const res = await authRouteCheck(authCache, { permissionFns: [CallTakingEnabledForAccount] });
          return res.allowed ? null : res.redirect;
        }
      },
      {
        path: '/app',
        element: <AppRoot />,
        errorElement: <DefaultRouteErrorElement />,
        children: [
          {
            path: appRoutePaths.AccountUser,
            element: <AccountUserListRoute />,
            errorElement: <DefaultRouteErrorElement appBarTitle='Users' />,
            loader: async () => {
              const res = await authRouteCheck(authCache, { permissionFns: [ViewAccountUsers, ManageAccountUsers] });
              return res.allowed ? null : res.redirect;
            }
          },
          {
            path: `${appRoutePaths.AccountUser}/:id`,
            element: <AccountUserDetailRoute />,
            errorElement: <DefaultRouteErrorElement appBarTitle='User' />,
            loader: async () => {
              const res = await authRouteCheck(authCache, { permissionFns: [ViewAccountUsers, ManageAccountUsers] });
              return res.allowed ? null : res.redirect;
            }
          },
          {
            path: appRoutePaths.Account,
            element: <AccountListRoute />,
            errorElement: <DefaultRouteErrorElement appBarTitle='Accounts' />,
            loader: async () => {
              const res = await authRouteCheck(authCache, { permissionFns: [ViewAccounts] });
              return res.allowed ? null : res.redirect;
            }
          },
          {
            path: `${appRoutePaths.Account}/:id`,
            element: <AccountDetailRoute />,
            errorElement: <DefaultRouteErrorElement appBarTitle='Account Detail' />,
            loader: async () => {
              const res = await authRouteCheck(authCache, { permissionFns: [ViewAccounts] });
              return res.allowed ? null : res.redirect;
            }
          },
          {
            path: appRoutePaths.AiCall,
            element: <AiCallListRoute />,
            errorElement: <DefaultRouteErrorElement appBarTitle='CPI Calls' />,
            loader: async () => {
              const res = await authRouteCheck(authCache, { permissionFns: [CallTakingEnabledForAccount] });
              return res.allowed ? null : res.redirect;
            }
          },
          {
            path: `${appRoutePaths.AiCall}/:id`,
            element: <AiCallDetailRoute />,
            errorElement: <DefaultRouteErrorElement appBarTitle='CPI Call Detail' />,
            loader: async () => {
              const res = await authRouteCheck(authCache, { permissionFns: [CallTakingEnabledForAccount] });
              return res.allowed ? null : res.redirect;
            }
          },
          {
            path: appRoutePaths.CallCenter,
            element: <CallCenterListRoute />,
            errorElement: <DefaultRouteErrorElement appBarTitle='Call Centers' />,
            loader: async () => {
              const res = await authRouteCheck(authCache, { permissionFns: [SystemUser] });
              return res.allowed ? null : res.redirect;
            }
          },
          {
            path: `${appRoutePaths.CallCenter}/:id`,
            element: <CallCenterDetailRoute />,
            errorElement: <DefaultRouteErrorElement appBarTitle='Call Center Detail' />,
            loader: async () => {
              const res = await authRouteCheck(authCache, { permissionFns: [SystemUser] });
              return res.allowed ? null : res.redirect;
            }
          },
          {
            path: appRoutePaths.Company,
            element: <CompanyListRoute />,
            errorElement: <DefaultRouteErrorElement appBarTitle='Companies' />,
            loader: async () => {
              const res = await authRouteCheck(authCache, { permissionFns: [ManageCompanies] });
              return res.allowed ? null : res.redirect;
            }
          },
          {
            path: `${appRoutePaths.Company}/:id`,
            element: <CompanyDetailRoute />,
            errorElement: <DefaultRouteErrorElement appBarTitle='Company Detail' />,
            loader: async () => {
              const res = await authRouteCheck(authCache, { permissionFns: [ManageCompanies] });
              return res.allowed ? null : res.redirect;
            }
          },
          {
            path: appRoutePaths.SystemAdministration,
            element: <SystemAdministrationRoute />,
            errorElement: <DefaultRouteErrorElement appBarTitle='System Administration' />,
            loader: async () => {
              const res = await authRouteCheck(authCache, { permissionFns: [SystemUser] });
              return res.allowed ? null : res.redirect;
            }
          },
          {
            path: appRoutePaths.CallLog,
            element: <PhoneCallListRoute />,
            errorElement: <DefaultRouteErrorElement appBarTitle='Call Log' />,
            loader: async () => {
              const res = await authRouteCheck(authCache, { permissionFns: [CallTakingEnabledForAccount] });
              return res.allowed ? null : res.redirect;
            }
          },
          {
            path: `${appRoutePaths.CallLog}/:id`,
            element: <PhoneCallDetailRoute />,
            errorElement: <DefaultRouteErrorElement appBarTitle='Phone Call Detail' />,
            loader: async () => {
              const res = await authRouteCheck(authCache, { permissionFns: [CallTakingEnabledForAccount] });
              return res.allowed ? null : res.redirect;
            }
          },
          {
            path: appRoutePaths.Profile,
            element: <ProfileRoute />,
            errorElement: <DefaultRouteErrorElement appBarTitle='My Profile' />,
            loader: async () => {
              const res = await authRouteCheck(authCache);
              return res.allowed ? null : res.redirect;
            }
          },
          {
            path: appRoutePaths.MyCallCenter,
            element: <MyCallCenterRoute />,
            errorElement: <DefaultRouteErrorElement appBarTitle='My Call Center' />,
            loader: async () => {
              const res = await authRouteCheck(authCache, { permissionFns: [CallCenterAdmin] });
              return res.allowed ? null : res.redirect;
            }
          },
          {
            path: appRoutePaths.User,
            element: <UserListRoute />,
            errorElement: <DefaultRouteErrorElement appBarTitle='Users' />,
            loader: async () => {
              const res = await authRouteCheck(authCache, {
                permissionFns: [ViewCallCenterUsers, ManageCallCenterUsers]
              });
              return res.allowed ? null : res.redirect;
            }
          },
          {
            path: `${appRoutePaths.User}/:id`,
            element: <UserDetailRoute />,
            errorElement: <DefaultRouteErrorElement appBarTitle='User' />,
            loader: async () => {
              const res = await authRouteCheck(authCache, {
                permissionFns: [ViewCallCenterUsers, ManageCallCenterUsers]
              });
              return res.allowed ? null : res.redirect;
            }
          },
          {
            path: appRoutePaths.Forbidden,
            element: <ForbiddenRoute />,
            errorElement: <DefaultRouteErrorElement appBarTitle='Forbidden' />
          },
          { path: '', element: <Redirects /> }
        ]
      },
      { path: '', element: <Redirects /> }
    ]
  },
  { path: '*', element: <Redirects /> }
];

const getCallPilotRouter = (authCache: AuthResourceConfig) => sentryCreateBrowserRouter(getRoutes(authCache));

export const CallPilotRouterProvider = (props: { AuthResources: AuthResourceConfig }) => {
  return <RouterProvider router={getCallPilotRouter(props.AuthResources)} />;
};
